import {
  HttpClient,
  HttpHeaders,
  HttpParams,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from '@environments/environment';

/** JSONリクエストオプション */
interface CommonRequestOptions {
  headers?:
    | HttpHeaders
    | {
        [header: string]: string | string[];
      };

  params?:
    | HttpParams
    | {
        [param: string]:
          | string
          | number
          | boolean
          | ReadonlyArray<string | number | boolean>;
      };
}

export interface JsonRequestOptions extends CommonRequestOptions {
  observe?: 'body';
  responseType?: 'json';
}

export interface BlobRequestOptions extends CommonRequestOptions {
  observe: 'response';
  responseType: 'blob';
}

/**
 * APIサービス
 */
@Injectable({
  providedIn: 'root',
})
export class ApiService {
  /** バックエンドエンドポイント */
  private endpoint = environment.backendEndpoint;

  /**
   * コンストラクタ
   * @param http HTTPクライアント
   */
  constructor(private http: HttpClient) {}

  /**
   * GETリクエスト
   * @param path URLパス
   * @param options オプション
   * @param skipLoading ローディング表示をスキップするか
   * @returns レスポンス
   */
  public get<T = unknown>(
    path: string,
    options?: JsonRequestOptions,
    skipLoading?: boolean,
  ): Observable<T>;

  public get(
    path: string,
    options?: BlobRequestOptions,
    skipLoading?: boolean,
  ): Observable<HttpResponse<Blob>>;

  public get<T = unknown>(
    path: string,
    options: JsonRequestOptions | BlobRequestOptions = {
      responseType: 'json',
      observe: 'body',
    },
    skipLoading = false,
  ): Observable<T | HttpResponse<Blob>> {
    const url = `${this.endpoint}` + path;
    if (skipLoading) {
      options = this.addSkipLoadingOptions(options);
    }

    if (options.responseType === 'blob') {
      return this.http.get(url, options);
    } else {
      return this.http.get<T>(url, options);
    }
  }

  /**
   * POSTリクエスト
   * @param path URLパス
   * @param body リクエストボディ
   * @param options オプション
   * @param skipLoading ローディング表示をスキップするか
   * @returns レスポンス
   */
  public post<T = unknown>(
    path: string,
    body: unknown,
    options: JsonRequestOptions = { responseType: 'json', observe: 'body' },
    skipLoading = false,
  ): Observable<T> {
    const url = `${this.endpoint}` + path;
    if (skipLoading) {
      options = this.addSkipLoadingOptions(options);
    }
    return this.http.post<T>(url, body, options);
  }

  /**
   * PUTリクエスト
   * @param path URLパス
   * @param body リクエストボディ
   * @param options オプション
   * @returns レスポンス
   */
  public put<T = unknown>(
    path: string,
    body: unknown,
    options: JsonRequestOptions = { responseType: 'json', observe: 'body' },
  ): Observable<T> {
    const url = `${this.endpoint}` + path;
    return this.http.put<T>(url, body, options);
  }

  /**
   * DELETEリクエスト
   * @param path URLパス
   * @param options オプション
   * @returns レスポンス
   */
  public delete<T = unknown>(
    path: string,
    options: JsonRequestOptions = { responseType: 'json', observe: 'body' },
  ): Observable<T> {
    const url = `${this.endpoint}` + path;
    return this.http.delete<T>(url, options);
  }

  private addSkipLoadingOptions<
    T extends CommonRequestOptions = JsonRequestOptions,
  >(options: T): T {
    let headers = options.headers || {};
    if (headers instanceof HttpHeaders) {
      headers = headers.set('x-matp-skip-loading', 'true');
    } else {
      headers['x-matp-skip-loading'] = 'true';
    }
    options.headers = headers;
    return options;
  }
}
